import React, { useRef, useEffect, useState } from 'react';
import { useMediaCapture } from '../../hooks/useMediaCapture';
import { useCameraControls } from '../../hooks/useCameraControls';

/**
 * Captura de foto/video/audio a pantalla completa (README diseño, sección
 * 5.7). Reusa `useMediaCapture`/`useCameraControls` tal cual existían para
 * el flujo viejo — incluyen la marca de agua de fotos (fecha/hora, dirección,
 * clima) que ya funcionaba, no se reescribe esa lógica.
 */
const CapturaCamara = ({ modoInicial, locationData, marcaAguaConfig, onCapturado, onCancelar }) => {
  const [modo, setModo] = useState(modoInicial); // 'foto' | 'video' | 'audio'
  const videoRef = useRef(null);
  const camara = useCameraControls();
  const captura = useMediaCapture();

  const tipoMedia = modo === 'foto' ? 'photo' : modo;

  useEffect(() => {
    camara.startCamera(tipoMedia, null, 'environment');
    return () => { camara.stopCameraCompletely(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modo]);

  useEffect(() => {
    if (videoRef.current && camara.stream) {
      videoRef.current.srcObject = camara.stream;
    }
  }, [camara.stream]);

  useEffect(() => {
    if (captura.mediaBlob) {
      onCapturado({
        blob: captura.mediaBlob,
        blobUrl: captura.mediaBlobUrl,
        tipo: modo,
        duracion: captura.recordingTime,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captura.mediaBlob]);

  const handleObturador = async () => {
    if (modo === 'foto') {
      await captura.capturePhoto(videoRef, 'photo', camara.setError, locationData, marcaAguaConfig);
    } else if (captura.recording) {
      captura.stopRecording();
    } else {
      await captura.startRecording(camara.stream, modo, camara.setError);
    }
  };

  const formatearTiempo = (segundos) => {
    const m = Math.floor(segundos / 60).toString().padStart(2, '0');
    const s = (segundos % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  return (
    <div className="cc-pantalla">
      {modo === 'audio' ? (
        <div className="cc-audio-fondo">
          {captura.recording && <span className="cc-chip-rec">GRABANDO</span>}
          <div className="cc-cronometro">{formatearTiempo(captura.recordingTime)}</div>
        </div>
      ) : (
        <video ref={videoRef} autoPlay playsInline muted className="cc-video" />
      )}

      <div className="cc-chip-info">
        {new Date().toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' })}
        {locationData?.address && ` · ${locationData.address}`}
      </div>

      {modo === 'video' && captura.recording && (
        <span className="cc-chip-rec cc-chip-rec-video">
          REC {formatearTiempo(captura.recordingTime)}
        </span>
      )}

      {camara.error && <div className="cc-error">{camara.error}</div>}

      <div className="cc-selector-modo">
        {['foto', 'video', 'audio'].map((m) => (
          <button
            key={m}
            type="button"
            className={modo === m ? 'cc-modo-activo' : ''}
            disabled={captura.recording}
            onClick={() => setModo(m)}
          >
            {m === 'foto' ? 'Foto' : m === 'video' ? 'Video' : 'Audio'}
          </button>
        ))}
      </div>

      <div className="cc-controles-inferiores">
        <button type="button" className="cc-btn-cancelar" onClick={onCancelar}>
          <i className="fas fa-times"></i>
        </button>

        <button type="button" className="cc-obturador" onClick={handleObturador}>
          {captura.recording
            ? <span className="cc-obturador-stop"></span>
            : modo === 'foto'
              ? <i className="fas fa-camera"></i>
              : modo === 'audio'
                ? <i className="fas fa-microphone"></i>
                : <i className="fas fa-circle" style={{ color: '#C62828' }}></i>}
        </button>

        {modo !== 'audio' ? (
          <button
            type="button"
            className="cc-btn-flip"
            onClick={() => camara.switchCamera(tipoMedia, null)}
            disabled={captura.recording}
          >
            <i className="fas fa-sync-alt"></i>
          </button>
        ) : <div style={{ width: 46 }} />}
      </div>
    </div>
  );
};

export default CapturaCamara;
